import { useState, useEffect } from 'react';
import { matrixAPI, seriesAPI, divisionsAPI, teamsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, Spinner } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const RESTRICTION_OPTIONS = ['None', 'August only', 'July only', 'June only', 'No restriction', 'Must not play', 'Home only', 'Away only', 'Custom'];

const RESTRICTION_COLORS = {
  'None':           { bg: 'transparent',              text: 'var(--dim)',    border: 'var(--border)' },
  'August only':    { bg: 'rgba(245,166,35,.15)',      text: 'var(--gold)',   border: 'rgba(245,166,35,.4)' },
  'July only':      { bg: 'rgba(59,130,246,.15)',      text: 'var(--blue)',   border: 'rgba(59,130,246,.4)' },
  'June only':      { bg: 'rgba(168,85,247,.15)',      text: 'var(--purple)', border: 'rgba(168,85,247,.4)' },
  'Must not play':  { bg: 'rgba(239,68,68,.15)',       text: 'var(--red)',    border: 'rgba(239,68,68,.4)' },
  'Home only':      { bg: 'rgba(0,200,150,.15)',       text: 'var(--accent)', border: 'rgba(0,200,150,.4)' },
  'Away only':      { bg: 'rgba(0,200,150,.08)',       text: 'var(--accent)', border: 'rgba(0,200,150,.25)' },
  'Custom':         { bg: 'rgba(245,166,35,.08)',      text: 'var(--gold)',   border: 'rgba(245,166,35,.25)' },
};

export default function MatrixPage() {
  const { canEdit } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selSeries, setSelSeries] = useState('');
  const [selDivision, setSelDivision] = useState('');
  const [matrix, setMatrix] = useState({}); // { 'teamA_id:teamB_id': { restriction, note } }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeCell, setActiveCell] = useState(null); // { rowId, colId }
  const [customNote, setCustomNote] = useState('');

  useEffect(() => { loadSeries(); }, []);
  useEffect(() => { if (selSeries) loadDivisions(selSeries); }, [selSeries]);
  useEffect(() => { if (selDivision) loadTeamsAndMatrix(); }, [selDivision]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      if (data.length > 0) setSelSeries(data[0].id);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (sid) => {
    try {
      const { data } = await divisionsAPI.list(sid);
      setDivisions(data);
      if (data.length > 0) setSelDivision(data[0].id);
    } catch {}
  };

  const loadTeamsAndMatrix = async () => {
    setLoading(true);
    try {
      const { data: teamData } = await teamsAPI.list({ series_id: selSeries, division_id: selDivision });
      setTeams(teamData);
      const { data: matrixData } = await matrixAPI.get(selDivision);
      const map = {};
      matrixData.forEach(r => { map[`${r.team1_id}:${r.team2_id}`] = { restriction: r.restriction, note: r.note || '' }; });
      setMatrix(map);
      setDirty(false);
    } catch { toast.error('Failed to load matrix'); }
    finally { setLoading(false); }
  };

  const getCell = (t1id, t2id) => {
    return matrix[`${t1id}:${t2id}`] || matrix[`${t2id}:${t1id}`] || { restriction: 'None', note: '' };
  };

  const setCell = (t1id, t2id, restriction, note) => {
    const key = `${t1id}:${t2id}`;
    setMatrix(p => ({ ...p, [key]: { restriction, note: note || '' } }));
    setDirty(true);
  };

  const cycleCell = (t1id, t2id) => {
    if (!canEdit()) return;
    const cur = getCell(t1id, t2id).restriction;
    const opts = Object.keys(RESTRICTION_COLORS);
    const next = opts[(opts.indexOf(cur) + 1) % opts.length];
    setCell(t1id, t2id, next, '');
    if (next === 'Custom') setActiveCell({ rowId: t1id, colId: t2id });
  };

  const saveMatrix = async () => {
    if (!selDivision) return toast.error('Select a division');
    setSaving(true);
    try {
      const cells = Object.entries(matrix)
        .filter(([, v]) => v.restriction && v.restriction !== 'None')
        .map(([key, v]) => {
          const [team1_id, team2_id] = key.split(':');
          return { team1_id, team2_id, restriction: v.restriction, note: v.note || null };
        });
      await matrixAPI.save(selDivision, cells);
      toast.success('Matrix saved');
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const clearAll = () => { setMatrix({}); setDirty(true); };

  const restrictedPairs = Object.entries(matrix).filter(([, v]) => v.restriction && v.restriction !== 'None');

  return (
    <div>
      <PageHeader icon="⊞" title="Matchup Matrix"
        subtitle="Configure special matchup restrictions between teams. Click a cell to cycle through restriction types."
        actions={canEdit() && (
          <button className="btn btn-primary" onClick={saveMatrix} disabled={saving || !dirty}>
            {saving ? <><Spinner size={14} /> Saving...</> : dirty ? '● Save Matrix' : '✓ Saved'}
          </button>
        )}
      />

      {/* Filters */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input select" style={{ width: 180 }} value={selSeries} onChange={e => { setSelSeries(e.target.value); setSelDivision(''); }}>
          {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
        </select>
        <select className="input select" style={{ width: 180 }} value={selDivision} onChange={e => setSelDivision(e.target.value)}>
          <option value="">Select division...</option>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {teams.length > 0 && <span style={{ fontSize: 12, color: 'var(--mid)' }}>{teams.length} teams · {restrictedPairs.length} restrictions</span>}
        {dirty && canEdit() && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={clearAll}>Clear All</button>}

        {/* Legend */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(RESTRICTION_COLORS).filter(([k]) => k !== 'None').map(([label, col]) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: col.bg, border: `1px solid ${col.border}` }} />
              <span style={{ color: 'var(--mid)' }}>{label}</span>
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 60, display: 'flex', justifyContent: 'center' }}><Spinner size={28} /></div>
      ) : !selDivision ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--mid)', fontSize: 13 }}>Select a series and division to view the matrix.</div>
      ) : teams.length < 2 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--mid)', fontSize: 13 }}>Need at least 2 teams in this division to configure matchups.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16 }}>
          {/* Matrix grid */}
          <div className="card" style={{ padding: 16, overflowX: 'auto' }}>
            <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 12 }}>
              {canEdit() ? 'Click any cell to cycle restriction type. Diagonal = same team (skipped).' : 'Read-only view.'}
            </div>
            <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 80, padding: '6px 8px', textAlign: 'left', color: 'var(--mid)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Team ↓ vs →</th>
                  {teams.map(t => (
                    <th key={t.id} style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--accent)', fontFamily: 'monospace', fontWeight: 700, borderBottom: '1px solid var(--border)', minWidth: 70 }}>
                      {t.short_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teams.map((rowTeam, ri) => (
                  <tr key={rowTeam.id}>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {rowTeam.short_name}
                    </td>
                    {teams.map((colTeam, ci) => {
                      if (rowTeam.id === colTeam.id) {
                        return <td key={colTeam.id} style={{ background: 'rgba(255,255,255,.03)', borderBottom: '1px solid var(--border)' }} />;
                      }
                      // Show upper triangle only, mirror lower
                      if (ri > ci) {
                        const cell = getCell(colTeam.id, rowTeam.id);
                        const col = RESTRICTION_COLORS[cell.restriction] || RESTRICTION_COLORS['None'];
                        return (
                          <td key={colTeam.id} style={{ padding: '4px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                            <div style={{ width: 60, height: 30, borderRadius: 4, background: col.bg, border: `1px solid ${col.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: col.text, fontStyle: 'italic', opacity: 0.5 }}>
                              ↖ mirror
                            </div>
                          </td>
                        );
                      }
                      const cell = getCell(rowTeam.id, colTeam.id);
                      const col = RESTRICTION_COLORS[cell.restriction] || RESTRICTION_COLORS['None'];
                      const isActive = activeCell?.rowId === rowTeam.id && activeCell?.colId === colTeam.id;
                      return (
                        <td key={colTeam.id} style={{ padding: '4px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                          <div
                            onClick={() => cycleCell(rowTeam.id, colTeam.id)}
                            title={`${rowTeam.short_name} vs ${colTeam.short_name}: ${cell.restriction}${cell.note ? ' — ' + cell.note : ''}`}
                            style={{
                              width: 60, minHeight: 30, borderRadius: 4, padding: '4px 2px',
                              background: col.bg, border: `1.5px solid ${isActive ? 'var(--accent)' : col.border}`,
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                              cursor: canEdit() ? 'pointer' : 'default',
                              transition: 'all .1s',
                            }}>
                            <span style={{ fontSize: 9, color: col.text, fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                              {cell.restriction === 'None' ? '—' : cell.restriction}
                            </span>
                            {cell.note && <span style={{ fontSize: 8, color: 'var(--mid)', marginTop: 2, maxWidth: 56, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell.note}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Custom note input */}
            {activeCell && canEdit() && (
              <div style={{ marginTop: 16, padding: '12px 14px', background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--gold)', flexShrink: 0 }}>
                  Custom note for {teams.find(t => t.id === activeCell.rowId)?.short_name} vs {teams.find(t => t.id === activeCell.colId)?.short_name}:
                </span>
                <input className="input" style={{ flex: 1 }} placeholder="e.g. Only in August, specific ground..."
                  value={customNote}
                  onChange={e => setCustomNote(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setCell(activeCell.rowId, activeCell.colId, 'Custom', customNote);
                      setActiveCell(null); setCustomNote('');
                    }
                  }}
                />
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setCell(activeCell.rowId, activeCell.colId, 'Custom', customNote);
                  setActiveCell(null); setCustomNote('');
                }}>Set</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setActiveCell(null); setCustomNote(''); }}>Cancel</button>
              </div>
            )}

            {dirty && canEdit() && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 7, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--gold)' }}>● Unsaved changes</span>
                <button className="btn btn-primary btn-sm" onClick={saveMatrix} disabled={saving}>{saving ? 'Saving...' : 'Save Now'}</button>
              </div>
            )}
          </div>

          {/* Restrictions summary sidebar */}
          <div style={{ width: 240 }}>
            <div className="card" style={{ padding: 16 }}>
              <SectionTitle color="var(--purple)">Active Restrictions</SectionTitle>
              {restrictedPairs.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--dim)', padding: '8px 0' }}>No restrictions set.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {restrictedPairs.map(([key, val]) => {
                    const [t1id, t2id] = key.split(':');
                    const t1 = teams.find(t => t.id === t1id);
                    const t2 = teams.find(t => t.id === t2id);
                    if (!t1 || !t2) return null;
                    const col = RESTRICTION_COLORS[val.restriction] || RESTRICTION_COLORS['None'];
                    return (
                      <div key={key} style={{ padding: '8px 10px', background: col.bg, border: `1px solid ${col.border}`, borderRadius: 6 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span className="mono" style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>{t1.short_name}</span>
                          <span style={{ fontSize: 10, color: 'var(--dim)' }}>vs</span>
                          <span className="mono" style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>{t2.short_name}</span>
                        </div>
                        <div style={{ fontSize: 10, color: col.text, fontWeight: 700, marginTop: 3 }}>{val.restriction}</div>
                        {val.note && <div style={{ fontSize: 10, color: 'var(--mid)', marginTop: 2 }}>{val.note}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card" style={{ padding: 16, marginTop: 12 }}>
              <SectionTitle color="var(--mid)">How to use</SectionTitle>
              <div style={{ fontSize: 11, color: 'var(--mid)', lineHeight: 1.8 }}>
                <div>• Click a cell to cycle restriction</div>
                <div>• Diagonal cells are skipped</div>
                <div>• Lower triangle mirrors upper</div>
                <div>• "Custom" opens a note field</div>
                <div>• Save when done</div>
                <div style={{ marginTop: 8, color: 'var(--dim)' }}>Restrictions are read by the schedule generator when building fixtures.</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
